import {
  IErrorApiResponse,
  ISuccessApiResponse,
  IValidationError,
} from '@wyz/types';
import {
  CrudOptions,
  OverridableQuery,
  restrictApi,
  serialize,
  useCrud,
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@wyz/utils';
import { isNil } from 'lodash-es';
import { useApi, useConfig } from '~/common/context';
import { CommonErrorCodesEnumType } from '~/common/types';
import {
  CustomerCriterion,
  ICriteriaUsage,
  ICriteriaUsageVariable,
} from '~/connect/types';
import { ConnectQueryKeys } from '~/connect/api';
import {
  IRedistributionBrandTemplatePayload,
  IRedistributionBrandTemplateWithBrands,
} from '~/connect/types';

type Actions = 'create' | 'update' | 'getOneById' | 'remove';

type Options = Pick<
  CrudOptions<
    IRedistributionBrandTemplateWithBrands,
    IRedistributionBrandTemplatePayload,
    IRedistributionBrandTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >,
  Actions
> & {
  getMatched?: OverridableQuery<
    UseQueryOptions<
      ISuccessApiResponse<IRedistributionBrandTemplateWithBrands | null>,
      IErrorApiResponse<unknown, CommonErrorCodesEnumType>
    >
  > & {
    args?: {
      customerCriteria: Array<CustomerCriterion>;
      customerInternalCriteria: Array<CustomerCriterion>;
    };
  };
  checkCriteriaUsage?: UseMutationOptions<
    ISuccessApiResponse<ICriteriaUsage>,
    IErrorApiResponse,
    ICriteriaUsageVariable
  >;
};

export const useManageRedistributionBrandTemplates = <
  TRequestedKeys extends keyof Options,
  TRequestedOptions extends Options = Pick<Options, TRequestedKeys>,
>(
  options: TRequestedOptions,
) => {
  const { callCoreApi } = useApi();
  const { tenantCode } = useConfig();

  const api = useCrud<
    Actions,
    IRedistributionBrandTemplateWithBrands,
    IRedistributionBrandTemplatePayload,
    IRedistributionBrandTemplatePayload,
    IErrorApiResponse<
      IValidationError<CommonErrorCodesEnumType>,
      CommonErrorCodesEnumType
    >
  >(
    {
      baseKey: ConnectQueryKeys.redistributionBrandTemplate.base(),
      caller: callCoreApi,
      endpoint: `/tenants/api/wtb/tenants/${tenantCode}/redistributions/brand-templates`,
    },
    {
      update: {
        ...options.update,
        keysToInvalidateOnSuccess: [
          ConnectQueryKeys.redistributionBrandTemplate.base(),
        ],
      },
      ...options,
    },
  );

  const getMatchedQuery = useQuery({
    queryKey: ConnectQueryKeys.redistributionBrandTemplate.match(
      options.getMatched?.args,
    ),
    queryFn: () =>
      callCoreApi<
        ISuccessApiResponse<IRedistributionBrandTemplateWithBrands | null>
      >(
        `/tenants/api/wtb/tenants/${tenantCode}/redistributions/brand-templates/matched?${serialize(
          options.getMatched?.args,
        )}`,
      ),
    ...options.getMatched,
    enabled:
      !isNil(options.getMatched?.args?.customerCriteria) &&
      Boolean(options.getMatched.enabled),

    retry: false,
    gcTime: 0,
  });

  const checkCriteriaUsage = useMutation({
    mutationFn: (variables) =>
      callCoreApi<ISuccessApiResponse<ICriteriaUsage>>(
        `/tenants/api/wtb/tenants/${tenantCode}/redistributions/brand-templates/${
          variables.id
        }/check-usage?${serialize(variables.query)}`,
      ),
    ...options.checkCriteriaUsage,
  });

  return restrictApi(options, {
    ...api,
    getMatched: getMatchedQuery,
    checkCriteriaUsage,
  });
};
